// Subtasks on the task page: the parent's progress panel with its table and
// "Add subtask" form, and the "Subtask x of <parent>" line on a subtask
// (docs/plans/2026-10-03-subtaken.md §9).

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOpenTask } from './task-links'
import type {
  SubtaskDraftInput,
  SubtaskPhase,
  SubtaskSummary,
  SubtaskView,
  Task,
} from '@goblic/shared'
import { SUBTASK_KEY_PATTERN } from '@goblic/shared'
import { ApiError, tasksApi, workflowsApi } from '../../lib/api-client'
import { formatMoney, humanize } from '../../lib/format'
import { useDialogs } from '../dialogs'
import { StatusBadge } from '../status-badge'
import { Field, Notice } from '../ui'

/** How often the panel refreshes while a subtask is running. */
const LIVE_REFRESH_MS = 4000

export const PHASE_LABEL: Record<SubtaskPhase, string> = {
  draft: 'Draft',
  running: 'Running',
  succeeded: 'Done',
  failed: 'Failed',
}

/** "3/14" for the task list; null for a task without subtasks. */
export function subtaskBadgeText(summary: SubtaskSummary | null | undefined): string | null {
  return summary && summary.total > 0 ? `${summary.succeeded}/${summary.total}` : null
}

/** Small "done/total" badge for a task row. */
export function SubtaskCountBadge({ summary }: { summary: SubtaskSummary | null | undefined }) {
  const text = subtaskBadgeText(summary)
  if (!text) return null
  return (
    <span
      className="ml-2 rounded-full border border-line px-2 py-0.5 text-sm font-normal text-ink-2"
      title="Subtasks done / total"
    >
      {text} subtasks
    </span>
  )
}

export interface SubtaskSegment {
  key: 'succeeded' | 'running' | 'failed' | 'drafts'
  count: number
  className: string
}

/** Progress-bar segments of a card, in order done, running, failed/blocked, drafts; empty ones left out. A blocked subtask counts as blocked, not running. */
export function subtaskSegments(summary: SubtaskSummary): SubtaskSegment[] {
  const attention = summary.failed + summary.blocked
  const all: SubtaskSegment[] = [
    { key: 'succeeded', count: summary.succeeded, className: 'bg-ok' },
    { key: 'running', count: Math.max(summary.running - summary.blocked, 0), className: 'bg-info' },
    { key: 'failed', count: attention, className: 'bg-danger' },
    { key: 'drafts', count: summary.drafts, className: 'bg-line-strong' },
  ]
  return all.filter((segment) => segment.count > 0)
}

/** Spelled-out counts for a card's tooltip and aria-label: "2 of 5 subtasks done, 1 running, 1 failed, 1 draft". */
export function subtaskCardLabel(summary: SubtaskSummary): string {
  const parts = [
    `${summary.succeeded} of ${summary.total} subtasks done`,
    ...(summary.running - summary.blocked > 0 ? [`${summary.running - summary.blocked} running`] : []),
    ...(summary.blocked > 0 ? [`${summary.blocked} blocked`] : []),
    ...(summary.failed > 0 ? [`${summary.failed} failed`] : []),
    ...(summary.drafts > 0 ? [`${summary.drafts} ${summary.drafts === 1 ? 'draft' : 'drafts'}`] : []),
  ]
  return parts.join(', ')
}

/** Compact "2/5 subtasks" with a thin segmented bar for a board card. Nothing without subtasks. */
export function SubtaskCardSummary({ summary }: { summary: SubtaskSummary | null | undefined }) {
  const text = subtaskBadgeText(summary)
  if (!summary || !text) return null
  const label = subtaskCardLabel(summary)
  return (
    <div className="mt-1.5" title={label} role="group" aria-label={label}>
      <p className="text-xs text-ink-3">
        <span className="tabular-nums">{text}</span> subtasks
      </p>
      <div className="mt-0.5 flex h-1 gap-px overflow-hidden rounded-full bg-line" aria-hidden>
        {subtaskSegments(summary).map((segment) => (
          <div key={segment.key} className={segment.className} style={{ flexGrow: segment.count, flexBasis: 0 }} />
        ))}
      </div>
    </div>
  )
}

/** On a subtask: which task it belongs to. Renders nothing for a top-level task. */
export function SubtaskParentLine({ task }: { task: Task }) {
  const openTask = useOpenTask()
  const parentQuery = useQuery({
    queryKey: ['task', task.parentTaskId],
    queryFn: () => tasksApi.get(task.parentTaskId!),
    enabled: task.parentTaskId != null,
  })
  if (!task.parentTaskId) return null
  return (
    <p className="text-base text-ink-2">
      Subtask <span className="font-mono">{task.subtaskKey ?? ''}</span> of{' '}
      <button type="button" className="link" onClick={() => openTask(task.parentTaskId!)}>
        {parentQuery.data?.title ?? 'the main task'}
      </button>
    </p>
  )
}

/** Percentage of subtasks done (0 without subtasks). */
export function subtaskProgressPercent(summary: SubtaskSummary): number {
  return summary.total > 0 ? Math.round((summary.succeeded / summary.total) * 100) : 0
}

/** The breakdown after "x of y done": running, blocked and failed only when non-zero, then drafts. */
export function subtaskBreakdownParts(summary: SubtaskSummary): string[] {
  return [
    `${summary.running} running`,
    ...(summary.blocked > 0 ? [`${summary.blocked} blocked`] : []),
    ...(summary.failed > 0 ? [`${summary.failed} failed`] : []),
    `${summary.drafts} drafts`,
  ]
}

function SummaryBar({ summary }: { summary: SubtaskSummary }) {
  const pct = subtaskProgressPercent(summary)
  const parts = subtaskBreakdownParts(summary)
  return (
    <div className="mb-3">
      <div
        className="h-2 overflow-hidden rounded-full bg-surface-2"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={summary.total}
        aria-valuenow={summary.succeeded}
        aria-label="Subtasks done"
      >
        <div className="h-full bg-moss" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-1 text-sm text-ink-3">
        {summary.succeeded} of {summary.total} done · {parts.join(' · ')}
      </p>
    </div>
  )
}

/**
 * The "Subtasks" panel of a task that has subtasks or sits in a `subtasks`
 * workflow step. Renders nothing for any other task.
 */
export function SubtasksPanel({ task }: { task: Task }) {
  const openTask = useOpenTask()
  const queryClient = useQueryClient()
  const { confirm } = useDialogs()
  const [adding, setAdding] = useState(false)

  // Shared with the workflow section of the task page (same key).
  const workflowsQuery = useQuery({
    queryKey: ['project', task.projectId, 'workflows'],
    queryFn: () => workflowsApi.get(task.projectId),
    enabled: task.workflowKey != null,
  })
  const currentState = workflowsQuery.data?.workflows
    .find((w) => w.key === task.workflowKey)
    ?.states.find((s) => s.name === task.workflowState)
  const relevant =
    (task.subtaskSummary?.total ?? 0) > 0 || currentState?.kind === 'subtasks'

  const subtasksQuery = useQuery({
    queryKey: ['task', task.id, 'subtasks'],
    queryFn: () => tasksApi.subtasks(task.id),
    enabled: relevant,
    refetchInterval: (query) =>
      (query.state.data?.summary.running ?? 0) > 0 ? LIVE_REFRESH_MS : false,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['task', task.id, 'subtasks'] })
    void queryClient.invalidateQueries({ queryKey: ['tasks'] })
  }
  const deleteMutation = useMutation({
    mutationFn: (subtaskId: string) => tasksApi.deleteSubtask(task.id, subtaskId),
    onSuccess: refresh,
  })

  if (!relevant) return null
  const data = subtasksQuery.data
  const subtasks = data?.subtasks ?? []
  // The PR and cost columns only earn their width once a subtask has either.
  const showPr = subtasks.some((s) => s.pullRequestUrl)
  const showCost = subtasks.some((s) => s.costCents > 0)

  async function remove(subtask: SubtaskView): Promise<void> {
    const ok = await confirm({
      kind: 'delete',
      title: `Delete subtask ${subtask.key}?`,
      message: `“${subtask.title}” is a draft that hasn’t started. This can’t be undone.`,
      confirmLabel: 'Delete',
    })
    if (ok) deleteMutation.mutate(subtask.id)
  }

  return (
    <section>
      <div className="mb-1.5 flex items-center justify-between">
        <h3 className="text-sm font-medium text-ink-3">Subtasks</h3>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => setAdding((v) => !v)}
        >
          {adding ? 'Cancel' : 'Add subtask'}
        </button>
      </div>
      {currentState?.kind === 'subtasks' ? (
        <p className="mb-2 text-sm text-ink-3">
          Waiting for subtasks: this step finishes when they all have.
        </p>
      ) : null}
      {subtasksQuery.isError ? (
        <Notice tone="error">Couldn’t load the subtasks.</Notice>
      ) : null}
      {data ? <SummaryBar summary={data.summary} /> : null}
      {adding ? (
        <AddSubtaskForm
          task={task}
          siblings={subtasks}
          onDone={() => {
            setAdding(false)
            refresh()
          }}
        />
      ) : null}
      {data && subtasks.length === 0 ? (
        <p className="text-base text-ink-3">No subtasks yet.</p>
      ) : subtasks.length > 0 ? (
        <div className="overflow-x-auto rounded-control border border-line">
          <table className="data-table [&_td]:px-1.5 [&_th]:px-1.5">
            <thead>
              <tr>
                <th>Key</th>
                <th>Title</th>
                <th>Status</th>
                <th className="max-sm:hidden">Waits for</th>
                {showPr ? <th>PR</th> : null}
                {showCost ? <th>Cost</th> : null}
                <th className="w-px" />
              </tr>
            </thead>
            <tbody>
              {subtasks.map((s) => (
                <tr key={s.id}>
                  <td className="font-mono text-sm">{s.key}</td>
                  <td>
                    <button type="button" className="link text-left" onClick={() => openTask(s.id)}>
                      {s.title}
                    </button>
                    {s.blockedBy.length ? (
                      <span className="mt-0.5 block font-mono text-sm text-ink-3 sm:hidden">
                        waits for {s.blockedBy.join(', ')}
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <StatusBadge status={s.status} label={PHASE_LABEL[s.phase]} />
                    {s.workflowState ? (
                      <span className="mt-0.5 block text-sm text-ink-3">
                        {humanize(s.workflowState)}
                      </span>
                    ) : null}
                  </td>
                  <td className="font-mono text-sm max-sm:hidden">
                    {s.blockedBy.length ? s.blockedBy.join(', ') : '—'}
                  </td>
                  {showPr ? (
                    <td>
                      {s.pullRequestUrl ? (
                        <a href={s.pullRequestUrl} target="_blank" rel="noreferrer" className="link">
                          Pull request
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  ) : null}
                  {showCost ? <td className="whitespace-nowrap">{formatMoney(s.costCents)}</td> : null}
                  <td className="text-right">
                    {s.phase === 'draft' ? (
                      <button
                        type="button"
                        className="btn-ghost btn-sm"
                        disabled={deleteMutation.isPending}
                        onClick={() => void remove(s)}
                      >
                        Delete
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {deleteMutation.isError ? (
        <Notice tone="error">
          {deleteMutation.error instanceof ApiError
            ? deleteMutation.error.message
            : 'Couldn’t delete the subtask.'}
        </Notice>
      ) : null}
    </section>
  )
}

/** Splits a textarea into trimmed, non-empty lines. */
export function lines(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
}

function AddSubtaskForm({
  task,
  siblings,
  onDone,
}: {
  task: Task
  siblings: SubtaskView[]
  onDone: () => void
}) {
  const [key, setKey] = useState('')
  const [title, setTitle] = useState('')
  const [instructions, setInstructions] = useState('')
  const [criteria, setCriteria] = useState('')
  const [workflow, setWorkflow] = useState('')
  const [dependsOn, setDependsOn] = useState<string[]>([])

  const workflowsQuery = useQuery({
    queryKey: ['project', task.projectId, 'workflows'],
    queryFn: () => workflowsApi.get(task.projectId),
  })
  const workflows = workflowsQuery.data?.workflows ?? []

  const keyOk = SUBTASK_KEY_PATTERN.test(key)
  const create = useMutation({
    mutationFn: () => {
      const body: SubtaskDraftInput = {
        key,
        title: title.trim(),
        instructions: instructions.trim(),
        acceptanceCriteria: lines(criteria),
        workflow: workflow || null,
        dependsOn,
      }
      return tasksApi.createSubtask(task.id, body)
    },
    onSuccess: onDone,
  })

  return (
    <form
      className="mb-3 space-y-3 rounded-control border border-line-strong p-3"
      onSubmit={(event) => {
        event.preventDefault()
        if (keyOk && title.trim() && instructions.trim()) create.mutate()
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Key"
          htmlFor="subtask-key"
          hint="Lowercase letters, digits, - and _; unique within this task."
        >
          <input
            id="subtask-key"
            className="field-input font-mono"
            value={key}
            maxLength={40}
            onChange={(event) => setKey(event.target.value)}
          />
        </Field>
        <Field label="Title" htmlFor="subtask-title">
          <input
            id="subtask-title"
            className="field-input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>
      </div>
      <Field label="Instructions" htmlFor="subtask-instructions">
        <textarea
          id="subtask-instructions"
          className="field-input min-h-24"
          value={instructions}
          onChange={(event) => setInstructions(event.target.value)}
        />
      </Field>
      <Field label="Done when (one per line, optional)" htmlFor="subtask-criteria">
        <textarea
          id="subtask-criteria"
          className="field-input min-h-16"
          value={criteria}
          onChange={(event) => setCriteria(event.target.value)}
        />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Workflow (optional)" htmlFor="subtask-workflow">
          <select
            id="subtask-workflow"
            className="field-input"
            value={workflow}
            onChange={(event) => setWorkflow(event.target.value)}
          >
            <option value="">Default</option>
            {workflows.map((w) => (
              <option key={w.key} value={w.key}>
                {humanize(w.key)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Depends on (optional)" htmlFor="subtask-depends">
          <select
            id="subtask-depends"
            className="field-input h-24"
            multiple
            value={dependsOn}
            disabled={siblings.length === 0}
            onChange={(event) =>
              setDependsOn(Array.from(event.target.selectedOptions, (o) => o.value))
            }
          >
            {siblings.map((s) => (
              <option key={s.id} value={s.key}>
                {s.key}: {s.title}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {create.isError ? (
        <Notice tone="error">
          {create.error instanceof ApiError ? create.error.message : 'Couldn’t add the subtask.'}
        </Notice>
      ) : null}
      <div className="flex justify-end">
        <button
          type="submit"
          className="btn-primary"
          disabled={create.isPending || !keyOk || !title.trim() || !instructions.trim()}
        >
          {create.isPending ? 'Adding…' : 'Add subtask'}
        </button>
      </div>
    </form>
  )
}
